import { ComponentType, useState } from 'react';
import { FixedSizeList as _FixedSizeList_, FixedSizeListProps } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import {
  Button,
  Badge,
  Switch,
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@artstorefronts/ui';
import { Edit2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useMutation, gql } from '@apollo/client';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const List = _FixedSizeList_ as unknown as ComponentType<FixedSizeListProps>;

const TOGGLE_COMPLETION_TYPE = gql`
  mutation ToggleCompletionType($input: ToggleCompletionTypeInput!) {
    toggleCompletionType(input: $input) {
      id
      enabled
    }
  }
`;

export function SparkTable({ sparks }: any) {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const [loadingToggles, setLoadingToggles] = useState<Record<string, boolean>>({});
  const [confirmDialog, setConfirmDialog] = useState<{ id: string; open: boolean } | null>(null);

  const [toggleCompletionType] = useMutation(TOGGLE_COMPLETION_TYPE, {
    onError: (error) => {
      toast.error(t('sparks.table.toast-toggle-failed'));
      console.error('Toggle error:', error);
    },
  });

  function handleToggle(id: string, currentEnabled: boolean) {
    if (!currentEnabled) {
      executeToggle(id, currentEnabled);
      return;
    }

    setConfirmDialog({ id, open: true });
  }

  function executeToggle(id: string, currentEnabled: boolean) {
    setLoadingToggles((prev) => ({ ...prev, [id]: true }));

    setTimeout(() => {
      toggleCompletionType({
        variables: {
          input: { id },
        },
        optimisticResponse: {
          toggleCompletionType: {
            __typename: 'CompletionType',
            id,
            enabled: !currentEnabled,
          },
        },
      }).finally(() => {
        setLoadingToggles((prev) => ({ ...prev, [id]: false }));
      });
    }, 150);
  }

  const Row = ({ index, style }: any) => {
    const type = sparks[index];
    const isLoading = loadingToggles[type.id];

    function formatDate(dateString: string) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        timeZone: 'UTC',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      });
    }

    return (
      <div style={style} className="border-b bg-white">
        <div className="grid grid-cols-[80px_1fr_80px_2fr_1fr_1fr_auto] gap-x-2 items-center px-4 py-3">
          <div className="truncate text-sm font-medium">{type.id}</div>
          <div className="flex items-center gap-2 text-sm font-medium">{type.name}</div>
          <div className="text-muted-foreground truncate text-sm font-light">{type.order}</div>
          <div className="text-muted-foreground truncate text-sm font-light">{type.description}</div>
          <div className="flex gap-1 flex-wrap">
            <Badge variant="secondary">{type.planNames}</Badge>
          </div>
          <div className="text-muted-foreground truncate text-sm font-light">
            {type.expiresAt ? formatDate(type.expiresAt) : ''}
          </div>
          <div className="flex items-center justify-end gap-1">
            <Switch
              checked={type.enabled}
              onCheckedChange={() => !isLoading && handleToggle(type.id, type.enabled)}
              disabled={isLoading}
            />
            <Link to={`/admin/sparks/edit/${type.id}`}>
              <Button variant="ghost" size="icon">
                <Edit2 className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="rounded-md border overflow-hidden">
        <div className="bg-grey-50 border-b">
          <div className="grid grid-cols-[80px_1fr_80px_2fr_1fr_1fr_auto] gap-x-2 py-3 px-4 text-sm font-medium">
            <div>{t('sparks.table.id')}</div>
            <div>{t('sparks.table.name')}</div>
            <div>{t('sparks.table.order')}</div>
            <div>{t('sparks.table.description')}</div>
            <div>{t('sparks.table.plans')}</div>
            <div>{t('sparks.table.expires')}</div>
            <div className="flex justify-end">{t('sparks.table.actions')}</div>
          </div>
        </div>
        <div style={{ height: 'calc(100dvh - 500px)' }}>
          <AutoSizer>
            {({ height, width }) => (
              <List height={height} itemCount={sparks.length} itemSize={58} width={width}>
                {Row}
              </List>
            )}
          </AutoSizer>
        </div>
      </div>

      <AlertDialog open={confirmDialog?.open ?? false} onOpenChange={(open) => !open && setConfirmDialog(null)}>
        <AlertDialogContent className="sm:max-w-[400px]">
          <AlertDialogHeader className="text-center">
            <AlertDialogTitle>{t('sparks.table.disable-dialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('sparks.table.disable-dialog.description')}
              <br />
              {t('sparks.table.disable-dialog.confirm-question')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex justify-center gap-2">
            <Button variant="outline" onClick={() => setConfirmDialog(null)}>
              {tc('cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (confirmDialog) {
                  const spark = sparks.find((s: any) => s.id === confirmDialog.id);
                  executeToggle(confirmDialog.id, spark.enabled);
                  setConfirmDialog(null);
                }
              }}
            >
              {tc('disable')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
